import {Slot} from '@radix-ui/react-slot';
import {cva, VariantProps} from 'class-variance-authority';
import {ChevronDown, Copy, CopyCheck} from 'lucide-react';
import * as React from 'react';
import {twMerge as cn, twJoin} from 'tailwind-merge';

import {DropdownMenuTrigger} from '../dropdown';

const buttonVariants = cva(
  'inline-flex items-center gap-1.5 justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand ring-0 disabled:pointer-events-none disabled:opacity-50 outline-hidden',
  {
    variants: {
      variant: {
        default:
          'bg-active text-primary-foreground shadow-sm hover:bg-active/90',
        destructive:
          'bg-destructive text-destructive-foreground shadow-xs hover:bg-destructive/90',
        outline:
          'border border-brand bg-white shadow-xs hover:bg-brand/50 hover:text-accent-foreground',
        secondary:
          'bg-brand text-secondary-foreground hover:bg-active/10 hover:text-active backdrop-blur-xl',
        glass: 'bg-active/10 text-active hover:bg-active/20 hover:text-active',
        ghost:
          'hover:bg-brand/40 active:bg-brand/80 hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        emerald:
          'bg-emerald-500 text-primary-foreground shadow-sm hover:bg-emerald-500/80',
      },
      size: {
        default: 'h-9 px-4 py-2',
        responsive: 'h-9 px-0 py-0 w-9 xl:w-fit xl:px-4 xl:py-2',
        xs: 'h-6 rounded-md px-2 text-xs',
        sm: 'h-7 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-8',
        full: 'h-9 w-full px-4 py-2',
        icon: 'h-9 w-9 min-h-9 min-w-9',
        'icon-sm': 'h-8 w-8 min-h-8 min-w-8',
        shortcut: 'h-7 min-h-7 w-7 min-w-7',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({className, variant, size, asChild = false, ...props}, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({variant, size, className}))}
        ref={ref}
        {...props}
      />
    );
  },
);

Button.displayName = 'Button';

interface CopyButtonProps extends ButtonProps {
  reference?: string;
  copied?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  label?: string;
  disabled?: boolean;
}

const CopyButton = React.forwardRef<HTMLButtonElement, CopyButtonProps>(
  (
    {
      className,
      reference,
      onClick,
      size = 'shortcut',
      variant = 'ghost',
      label,
      disabled,
      ...props
    },
    ref,
  ) => {
    const [copyUpToDate, setCopyUpToDate] = React.useState<boolean>(false);

    React.useEffect(() => {
      if (reference) {
        setCopyUpToDate(false);
      }
    }, [reference]);

    return (
      <Button
        variant={variant}
        size={size}
        className={cn(
          copyUpToDate
            ? 'hover:text-emerald-600 hover:bg-emerald-200/50 text-emerald-600 bg-emerald-200/50'
            : '',
          className,
        )}
        ref={ref}
        disabled={disabled}
        {...props}
        onClick={(e) => {
          setCopyUpToDate(true);
          onClick?.(e);
        }}
      >
        {!!copyUpToDate ? (
          <CopyCheck className="size-4" />
        ) : (
          <Copy className="size-4" />
        )}
        {label && <span className="xl:block hidden">{label}</span>}
      </Button>
    );
  },
);

//Must be used in a DropdownMenu component as trigger
const ButtonAndDropdown = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (props, ref) => {
    const {children, onClick, ...otherProps} = props;
    return (
      <div className="flex flex-nowrap">
        <Button
          ref={ref}
          {...otherProps}
          className={twJoin(
            'rounded-r-none',
            (otherProps?.variant as string) === 'outline-solid' && 'border-r-0',
          )}
          onClick={onClick}
        >
          {children}
        </Button>
        <DropdownMenuTrigger asChild>
          <Button
            {...otherProps}
            size="icon"
            className="rounded-l-none border-l"
          >
            <ChevronDown className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
      </div>
    );
  },
);

export {Button, CopyButton, ButtonAndDropdown, buttonVariants};
